import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Plus } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import CreateStoryModal from './CreateStoryModal';
import StoryViewModal from './StoryViewModal';
import { toast } from 'sonner';

interface Story {
  id: string;
  user_id: string;
  content_url: string;
  content_type: string;
  caption: string | null;
  created_at: string;
  expires_at: string;
  profiles?: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  is_viewed?: boolean;
}

interface UserStories {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  stories: Story[];
  has_unviewed: boolean;
}

const StoriesSection = () => {
  const [userStories, setUserStories] = useState<UserStories[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedUserStories, setSelectedUserStories] = useState<UserStories | null>(null);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const { user, profile } = useAuth();

  useEffect(() => {
    fetchStories();
  }, [user]);

  const fetchStories = async () => {
    if (!user) return;

    try {
      // Get all active stories
      const { data: storiesData, error } = await supabase
        .from('stories')
        .select('*')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!storiesData || storiesData.length === 0) {
        setUserStories([]);
        setLoading(false);
        return;
      }

      // Get unique user IDs
      const userIds = Array.from(new Set(storiesData.map(story => story.user_id)));

      // Get profiles for these users
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url')
        .in('user_id', userIds);

      // Get story views for current user
      const { data: viewsData } = await supabase
        .from('story_views')
        .select('story_id')
        .eq('viewer_id', user.id);

      const viewedStoryIds = new Set(viewsData?.map(view => view.story_id) || []);

      // Group stories by user
      const groupedStories: Record<string, UserStories> = {};

      storiesData.forEach(story => {
        const userProfile = profilesData?.find(p => p.user_id === story.user_id);
        
        if (!groupedStories[story.user_id]) {
          groupedStories[story.user_id] = {
            user_id: story.user_id,
            username: userProfile?.username || 'Unknown',
            display_name: userProfile?.display_name,
            avatar_url: userProfile?.avatar_url,
            stories: [],
            has_unviewed: false
          };
        }

        const storyWithProfile = {
          ...story,
          profiles: userProfile,
          is_viewed: viewedStoryIds.has(story.id)
        };

        groupedStories[story.user_id].stories.push(storyWithProfile);
        
        // Check if user has unviewed stories
        if (!viewedStoryIds.has(story.id)) {
          groupedStories[story.user_id].has_unviewed = true;
        }
      });

      // Convert to array and sort (current user first, then by newest story)
      const storiesArray = Object.values(groupedStories).sort((a, b) => {
        if (a.user_id === user.id) return -1;
        if (b.user_id === user.id) return 1;
        
        const aLatest = Math.max(...a.stories.map(s => new Date(s.created_at).getTime()));
        const bLatest = Math.max(...b.stories.map(s => new Date(s.created_at).getTime()));
        
        return bLatest - aLatest;
      });

      setUserStories(storiesArray);
    } catch (error) {
      console.error('Error fetching stories:', error);
      toast.error('Errore nel caricamento delle storie');
    } finally {
      setLoading(false);
    }
  };

  const handleStoryCreated = () => {
    fetchStories();
    setShowCreateModal(false);
    toast.success('Storia creata con successo!');
  };

  const handleUserStoriesClick = (userStory: UserStories, startIndex = 0) => {
    setSelectedUserStories(userStory);
    setSelectedStoryIndex(startIndex);
    setShowViewModal(true);
  };

  const handleStoryViewed = async (storyId: string) => {
    if (!user) return;

    try {
      // Mark story as viewed
      await supabase
        .from('story_views')
        .upsert({
          story_id: storyId,
          viewer_id: user.id
        });

      // Update local state
      setUserStories(prev => 
        prev.map(userStory => ({
          ...userStory,
          stories: userStory.stories.map(story => 
            story.id === storyId ? { ...story, is_viewed: true } : story
          ),
          has_unviewed: userStory.stories.some(s => 
            s.id !== storyId && !s.is_viewed
          )
        }))
      );
    } catch (error) {
      console.error('Error marking story as viewed:', error);
    }
  };

  if (loading) {
    return (
      <div className="mb-6">
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex space-x-4 p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex-shrink-0">
                <div className="w-16 h-16 bg-muted rounded-full animate-pulse" />
                <div className="h-3 bg-muted rounded w-12 mt-2 animate-pulse" />
              </div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    );
  }

  // Check if current user has stories
  const currentUserStories = userStories.find(us => us.user_id === user?.id);

  return (
    <>
      <div className="mb-6">
        <ScrollArea className="w-full whitespace-nowrap">
          <div className="flex space-x-3 sm:space-x-4 p-3 sm:p-4">
            {/* Add Story Button (sempre primo) */}
            <div className="flex-shrink-0 text-center">
              <button
                onClick={() => setShowCreateModal(true)}
                className="relative w-16 h-16 sm:w-18 sm:h-18 rounded-full bg-gradient-to-br from-primary via-secondary to-tertiary p-0.5 hover:scale-110 transition-all duration-300 hover:shadow-primary animate-pulse-glow"
              >
                <div className="w-full h-full rounded-full bg-background flex items-center justify-center p-0.5">
                  <Avatar className="w-full h-full ring-2 ring-primary/30">
                    <AvatarImage src={user?.user_metadata?.avatar_url} />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white font-bold text-sm w-full h-full rounded-full flex items-center justify-center">
                      {profile?.display_name?.charAt(0).toUpperCase() || profile?.username?.charAt(0).toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center border-2 border-background shadow-lg">
                    <Plus className="w-3 h-3 text-white" />
                  </div>
                </div>
              </button>
              <p className="text-xs mt-2 text-muted-foreground/90 truncate w-16 font-medium">
                La tua storia
              </p>
            </div>

            {/* User Stories */}
            {userStories.map((userStory, index) => (
              <div key={userStory.user_id} className="flex-shrink-0 text-center animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <button
                  onClick={() => handleUserStoriesClick(userStory)}
                  className={`relative w-16 h-16 sm:w-18 sm:h-18 rounded-full p-0.5 hover:scale-110 transition-all duration-300 ${
                    userStory.has_unviewed
                      ? 'bg-gradient-to-br from-pink-500 via-purple-600 to-blue-500 shadow-lg hover:shadow-pink-500/50'
                      : 'bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 hover:shadow-gray-500/30'
                  }`}
                >
                  <div className="w-full h-full rounded-full bg-background p-0.5">
                    <Avatar className="w-full h-full">
                      <AvatarImage src={userStory.avatar_url || undefined} className="object-cover" />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white font-bold text-sm">
                        {userStory.username.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  {userStory.has_unviewed && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full border-2 border-background animate-pulse" />
                  )}
                </button>
                <p className="text-xs mt-2 text-muted-foreground/90 truncate w-16 font-medium">
                  {userStory.display_name || userStory.username}
                </p>
              </div>
            ))}
          </div>
          <ScrollBar orientation="horizontal" className="h-2" />
        </ScrollArea>
      </div>

      <CreateStoryModal 
        open={showCreateModal} 
        onOpenChange={setShowCreateModal}
        onStoryCreated={handleStoryCreated}
      />

      {selectedUserStories && (
        <StoryViewModal
          open={showViewModal}
          onOpenChange={setShowViewModal}
          userStories={selectedUserStories}
          startIndex={selectedStoryIndex}
          onStoryViewed={handleStoryViewed}
          onNext={() => {
            const currentIndex = userStories.findIndex(us => us.user_id === selectedUserStories.user_id);
            const nextIndex = currentIndex + 1;
            
            if (nextIndex < userStories.length) {
              handleUserStoriesClick(userStories[nextIndex], 0);
            } else {
              setShowViewModal(false);
            }
          }}
          onPrevious={() => {
            const currentIndex = userStories.findIndex(us => us.user_id === selectedUserStories.user_id);
            const prevIndex = currentIndex - 1;
            
            if (prevIndex >= 0) {
              const prevUserStories = userStories[prevIndex];
              handleUserStoriesClick(prevUserStories, prevUserStories.stories.length - 1);
            }
          }}
        />
      )}
    </>
  );
};

export default StoriesSection;